import React from "react";
import { CreditCard, CheckCircle2, ArrowRight, Phone } from "lucide-react";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";
import { Breadcrumbs, SectionHeading } from "@/components/common";
import { SITE } from "@/mock";

// Apply Now opens the Synchrony consumer application (MattressTown card powered by Synchrony)
const APPLY_URL = SITE.applyUrl;

const PARTNERS = [
  {
    name: "Synchrony",
    desc: "Apply for the MattressTown card, powered by Synchrony, and enjoy promotional financing that gives you more time to pay. Flexible revolving credit with simple monthly payments and fast approvals.",
  },
  {
    name: "Acima Leasing",
    desc: "Looking for a lease-to-own option? Acima makes it simple with no long-term commitments. Start with a small initial payment, enjoy your items right away, and choose a buyout option when you're ready.",
  },
];

const PERKS = [
  "Quick and easy applications",
  "Approvals designed to fit a wide range of budgets",
  "Flexible terms tailored to your needs",
  "Access to the products you want now without the wait",
];

export default function Financing() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Financing", path: "/financing" },
  ];
  return (
    <>
      <Seo
        title="Financing & Leasing - Easy Monthly Payments"
        description="Flexible financing and lease-to-own options at MattressTown in Fort Lauderdale through Synchrony and Acima. Fast approvals and simple monthly payments."
        path="/financing"
        jsonLd={breadcrumbJsonLd(crumbs)}
      />
      <Breadcrumbs items={crumbs} />

      <section className="relative overflow-hidden bg-[#0f1729]">
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 sm:px-6 sm:py-16 lg:grid-cols-2 lg:px-8">
          <div>
            <span className="inline-block rounded-full bg-[#D0021B] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white">
              Buy Now, Pay Over Time
            </span>
            <h1 className="mt-4 max-w-3xl text-4xl font-bold uppercase leading-tight text-white sm:text-5xl">
              Flexible Financing &amp; Leasing
            </h1>
            <p className="mt-4 max-w-2xl text-lg text-neutral-300">
              We make it easier to bring home what you need today with simple, trusted financing and
              leasing partners. Whether you're building your dream space or upgrading your setup,
              you can shop with confidence knowing you have choices that fit your budget.
            </p>
            <a
              href={APPLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="financing-hero-apply"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-9 py-4 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#a80016]"
            >
              <CreditCard className="h-5 w-5" aria-hidden="true" /> Apply Now
            </a>
          </div>
          <div className="flex justify-center lg:justify-end">
            <picture className="w-full sm:max-w-md lg:max-w-xl">
              <source media="(max-width: 1023px)" srcSet="/assets/mt-credit-card-tight.webp" />
              <img
                src="/assets/mt-credit-card-navy.webp"
                alt="MattressTown credit card powered by Synchrony"
                loading="eager"
                data-testid="financing-credit-card"
                className="w-full rounded-2xl transition-transform duration-500 hover:scale-[1.03]"
              />
            </picture>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Our Partners" title="Financing & Leasing Options" />
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {PARTNERS.map((p) => (
            <div
              key={p.name}
              data-testid={`financing-partner-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex flex-col rounded-2xl border border-neutral-200 bg-white p-7 shadow-sm"
            >
              <span className="h-1.5 w-12 rounded-full bg-[#D0021B]" aria-hidden="true" />
              <h2 className="mt-4 text-xl font-bold text-neutral-900">{p.name}</h2>
              <p className="mt-3 text-sm leading-relaxed text-neutral-600">{p.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-16">
          <SectionHeading eyebrow="Why Finance With Us" title="Simple, Flexible & Fast" />
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            {PERKS.map((p) => (
              <div key={p} className="flex items-start gap-3 rounded-xl border border-neutral-200 bg-white p-5">
                <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-[#D0021B]" aria-hidden="true" />
                <span className="font-medium text-neutral-800">{p}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center gap-4 rounded-2xl bg-cream p-8 text-center">
          <h2 className="text-2xl font-bold uppercase text-neutral-900">Ready to Apply?</h2>
          <p className="max-w-xl text-neutral-600">
            Take control of your purchase power. Apply today and choose the plan that works best for
            you - or call and our team will walk you through your options.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href={APPLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="financing-apply"
              className="inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#a80016]"
            >
              Apply Now <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <a
              href={SITE.phoneHref}
              className="inline-flex items-center gap-2 rounded-full border-2 border-neutral-900 px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-neutral-900 hover:bg-neutral-900 hover:text-white"
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
